// Generic field renderers for the onboarding wizard.
import { useRef, useState } from "react";
import { Loader2, Upload, X, FileText, Plus, Copy, Trash2, ArrowUp, ArrowDown, ChevronDown, ChevronUp, MapPin } from "lucide-react";
import { PACKAGE_GROUPS, CHARGE_STATUSES, CHARGE_UNITS, PRESET_CHARGES, REGIONS_BY_STATE, AU_STATES } from "./schema";

export const inputCls =
  "w-full p-2.5 rounded-xl bg-soft border border-border-neutral focus:border-mint focus:outline-none text-sm text-forest";

// ------------------------------------------------------------------ Field --
export function Field({ f, value, onChange, error, ctx, uploadProps }) {
  if (f.showIf && !f.showIf(ctx)) return null;
  const id = `f-${f.key}`;
  const inputId = `${id}-${Math.abs(hashCode(f.label))}`;
  return (
    <div className={f.half ? "" : "sm:col-span-2"} data-testid={`field-${f.key}`}>
      {f.type !== "checkbox" && (
        <label htmlFor={inputId} className="block text-sm font-bold text-forest mb-1">
          {f.label}{f.required && <span className="text-red-600 ml-0.5" aria-hidden>*</span>}
          {f.required && <span className="sr-only">(required)</span>}
        </label>
      )}
      <FieldInput f={f} value={value} onChange={onChange} inputId={inputId} error={error} uploadProps={uploadProps} ctx={ctx} />
      {f.help && !error && <p className="mt-1 text-xs text-mid-neutral">{f.help}</p>}
      {error && <p role="alert" className="mt-1 text-xs font-semibold text-red-600">{error}</p>}
    </div>
  );
}

function hashCode(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return h;
}

function FieldInput({ f, value, onChange, inputId, error, uploadProps, ctx }) {
  const v = value ?? "";
  const common = { id: inputId, "aria-invalid": !!error, "aria-describedby": error ? `${inputId}-err` : undefined };
  switch (f.type) {
    case "textarea":
      return (
        <>
          <textarea {...common} rows={3} value={v} maxLength={f.maxLen} placeholder={f.placeholder}
            onChange={(e) => onChange(e.target.value)} className={inputCls} />
          {f.maxLen && <p className="mt-0.5 text-[11px] text-mid-neutral text-right">{String(v).length}/{f.maxLen}</p>}
        </>
      );
    case "select":
      return (
        <select {...common} value={v} onChange={(e) => onChange(e.target.value)} className={inputCls}>
          <option value="">Select…</option>
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case "radio":
      return (
        <div role="radiogroup" aria-label={f.label} className="space-y-2">
          {f.options.map((o) => (
            <label key={o} className="flex items-start gap-2.5 text-sm text-forest cursor-pointer">
              <input type="radio" name={inputId} checked={v === o} onChange={() => onChange(o)} className="mt-0.5 accent-[#03221A]" />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
    case "yesno":
      return (
        <div role="radiogroup" aria-label={f.label} className="flex gap-2">
          {["Yes", "No"].map((o) => (
            <button key={o} type="button" onClick={() => onChange(o)} aria-pressed={v === o}
              className={"px-4 py-2 rounded-lg text-sm font-bold border transition-colors " +
                (v === o ? "bg-forest text-white border-forest" : "bg-white text-forest border-border-neutral hover:border-mint")}>
              {o}
            </button>
          ))}
        </div>
      );
    case "multiselect":
      return (
        <div className="flex flex-wrap gap-2" role="group" aria-label={f.label}>
          {f.options.map((o) => {
            const on = Array.isArray(v) && v.includes(o);
            return (
              <button key={o} type="button" aria-pressed={on}
                onClick={() => onChange(on ? v.filter((x) => x !== o) : [...(Array.isArray(v) ? v : []), o])}
                className={"px-3 py-1.5 rounded-full text-xs font-bold border transition-colors " +
                  (on ? "bg-forest text-white border-forest" : "bg-white text-forest border-border-neutral hover:border-mint")}>
                {o}
              </button>
            );
          })}
        </div>
      );
    case "checkbox":
      return (
        <label className="flex items-start gap-2.5 text-sm text-forest cursor-pointer">
          <input id={inputId} type="checkbox" checked={!!v} onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5 accent-[#03221A] h-4 w-4" aria-invalid={!!error} />
          <span>{f.label}{f.required && <span className="text-red-600 ml-0.5" aria-hidden>*</span>}</span>
        </label>
      );
    case "file":
      return <FileUpload inputId={inputId} fileIds={v ? [v] : []} multiple={false}
        onChange={(ids) => onChange(ids[0] || "")} uploadProps={uploadProps} />;
    case "files":
      return <FileUpload inputId={inputId} fileIds={Array.isArray(v) ? v : []} multiple
        onChange={onChange} uploadProps={uploadProps} />;
    case "number":
      return <input {...common} type="number" inputMode="decimal" min="0" value={v} placeholder={f.placeholder}
        onChange={(e) => onChange(e.target.value)} className={inputCls} />;
    case "date":
      return <input {...common} type="date" value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
    default:
      return <input {...common} type={f.type || "text"} value={v} placeholder={f.placeholder}
        onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  }
}

// ------------------------------------------------------------ File upload --
export function FileUpload({ inputId, fileIds, multiple, onChange, uploadProps }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { files, onUpload, onRemove, ready } = uploadProps;
  const metas = fileIds.map((id) => files.find((x) => x.id === id)).filter(Boolean);

  const handle = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setErr("");
    setBusy(true);
    for (const file of list) {
      try {
        const meta = await onUpload(file);
        onChange(multiple ? [...fileIds, meta.id] : [meta.id]);
        if (!multiple) break;
      } catch (ex) {
        setErr(ex.message || "Upload failed — please try again.");
        break;
      }
    }
    setBusy(false);
  };

  return (
    <div>
      <input ref={ref} id={inputId} type="file" multiple={multiple} className="hidden"
        accept=".pdf,.docx,.xlsx,.csv,.txt,.svg,.png,.jpg,.jpeg,.gif,.webp" onChange={handle} />
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => ready ? ref.current?.click() : setErr("Enter your direct email in Step 1 first — uploads attach to your saved draft.")}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-soft text-forest px-3 py-2 text-xs font-bold hover:bg-mint/20 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {busy ? "Uploading…" : multiple ? "Add file(s)" : "Choose file"}
        </button>
        <span className="text-[11px] text-mid-neutral">PDF, DOCX, XLSX, CSV, SVG, PNG or JPG · max 20 MB each</span>
      </div>
      {err && <p role="alert" className="mt-1 text-xs font-semibold text-red-600">{err}</p>}
      {metas.length > 0 && (
        <ul className="mt-2 space-y-1">
          {metas.map((m) => (
            <li key={m.id} className="flex items-center gap-2 rounded-lg bg-soft px-3 py-1.5 text-xs">
              <FileText className="h-3.5 w-3.5 text-mint shrink-0" />
              <span className="font-semibold text-forest truncate">{m.name}</span>
              <span className="text-mid-neutral shrink-0">{Math.max(1, Math.round(m.size / 1024))} KB</span>
              <button type="button" onClick={() => { onRemove(m.id); onChange(fileIds.filter((x) => x !== m.id)); }}
                aria-label={`Remove ${m.name}`}
                className="ml-auto text-red-600 hover:text-red-800"><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --------------------------------------------------------------- Repeater --
export function Repeater({ f, value, onChange, errors, uploadProps, titleOf }) {
  const items = Array.isArray(value) ? value : [];
  const [open, setOpen] = useState({});
  const set = (i, key, val) => onChange(items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i) => {
    if (window.confirm("Remove this entry? This cannot be undone.")) onChange(items.filter((_, idx) => idx !== i));
  };
  return (
    <div className="sm:col-span-2">
      <p className="block text-sm font-bold text-forest mb-1">{f.label}{f.required && <span className="text-red-600 ml-0.5">*</span>}</p>
      {f.help && <p className="mb-2 text-xs text-mid-neutral">{f.help}</p>}
      <div className="space-y-3">
        {items.map((item, i) => {
          const isOpen = open[i] !== false;
          return (
            <div key={i} className="rounded-xl border border-border-neutral bg-white p-4" data-testid={`${f.key}-item-${i}`}>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setOpen({ ...open, [i]: !isOpen })} aria-expanded={isOpen}
                  className="flex-1 flex items-center gap-2 text-left text-sm font-bold text-forest">
                  {isOpen ? <ChevronUp className="h-4 w-4 text-mint" /> : <ChevronDown className="h-4 w-4 text-mint" />}
                  {titleOf ? titleOf(item, i) : `Entry ${i + 1}`}
                </button>
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"
                  className="p-1.5 rounded-lg text-mid-neutral hover:text-forest disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down"
                  className="p-1.5 rounded-lg text-mid-neutral hover:text-forest disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => onChange([...items.slice(0, i + 1), { ...item }, ...items.slice(i + 1)])} aria-label="Duplicate entry"
                  className="p-1.5 rounded-lg text-mid-neutral hover:text-forest"><Copy className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => remove(i)} aria-label="Remove entry"
                  className="p-1.5 rounded-lg text-red-600 hover:text-red-800"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              {isOpen && (
                <div className="mt-3 grid sm:grid-cols-2 gap-4">
                  {f.itemFields.map((sf) => (
                    <Field key={sf.key} f={sf} value={item[sf.key]} ctx={item}
                      onChange={(val) => set(i, sf.key, val)}
                      error={errors?.[`${f.key}.${i}.${sf.key}`]} uploadProps={uploadProps} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button type="button" onClick={() => onChange([...items, {}])} data-testid={`${f.key}-add`}
        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold">
        <Plus className="h-4 w-4" /> {f.addLabel || "Add"}
      </button>
    </div>
  );
}

// --------------------------------------------------------- Packages editor --
export function PackagesEditor({ value, onChange, errors, uploadProps }) {
  const items = Array.isArray(value) ? value : [];
  const set = (i, next) => onChange(items.map((p, idx) => (idx === i ? next : p)));
  const remove = (i) => {
    if (window.confirm("Remove this package? This cannot be undone.")) onChange(items.filter((_, idx) => idx !== i));
  };
  return (
    <div className="space-y-4">
      {errors?.packages && <p role="alert" className="text-xs font-semibold text-red-600">{errors.packages}</p>}
      {items.map((p, i) => (
        <div key={i} className="rounded-2xl border border-border-neutral bg-white p-5" data-testid={`package-item-${i}`}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="text-sm font-extrabold text-forest">
              {p.customerName || p.internalName || `Package ${i + 1}`}
              {p.status && <span className={"ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold " + (p.status === "Active" ? "bg-lime text-forest" : "bg-soft text-mid-neutral")}>{p.status}</span>}
            </h3>
            <div className="flex gap-1">
              <button type="button" onClick={() => onChange([...items.slice(0, i + 1), { ...p }, ...items.slice(i + 1)])}
                aria-label="Duplicate package" title="Duplicate"
                className="p-1.5 rounded-lg text-mid-neutral hover:text-forest"><Copy className="h-4 w-4" /></button>
              <button type="button" onClick={() => remove(i)} aria-label="Remove package" title="Remove"
                className="p-1.5 rounded-lg text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          {PACKAGE_GROUPS.map((g) => {
            if (g.showIf && !g.showIf(p)) return null;
            return (
              <fieldset key={g.title} className="mb-5">
                <legend className="text-xs font-bold uppercase tracking-[0.14em] text-mint mb-3">{g.title}</legend>
                <div className="grid sm:grid-cols-2 gap-4">
                  {g.fields.map((sf) => (
                    <Field key={sf.key} f={sf} value={p[sf.key]} ctx={p}
                      onChange={(val) => set(i, { ...p, [sf.key]: val })}
                      error={errors?.[`packages.${i}.${sf.key}`]} uploadProps={uploadProps} />
                  ))}
                </div>
              </fieldset>
            );
          })}
        </div>
      ))}
      <button type="button"
        onClick={() => onChange([...items, { status: "Draft", category: "Solar only", retailGst: "Inclusive of GST" }])}
        data-testid="package-add"
        className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold">
        <Plus className="h-4 w-4" /> Add package
      </button>
    </div>
  );
}

// ---------------------------------------------------------- Charges editor --
export function ChargesEditor({ value, onChange, errors }) {
  const rows = Array.isArray(value) ? value : [];
  const byKey = Object.fromEntries(rows.filter((r) => r.key).map((r) => [r.key, r]));
  const custom = rows.filter((r) => !r.key);

  const upsert = (key, name, category, patch) => {
    const existing = byKey[key];
    const next = existing
      ? rows.map((r) => (r.key === key ? { ...r, ...patch } : r))
      : [...rows, { key, name, category, status: "", price: "", gst: "Inclusive of GST", unit: "each", trigger: "", approvalRequired: "", notes: "", customerWording: "", ...patch }];
    onChange(next);
  };
  const setCustom = (i, patch) => {
    const customs = custom.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
    onChange([...rows.filter((r) => r.key), ...customs]);
  };
  const removeCustom = (i) => {
    if (window.confirm("Remove this custom charge?")) onChange([...rows.filter((r) => r.key), ...custom.filter((_, idx) => idx !== i)]);
  };

  const doneCount = PRESET_CHARGES.filter(([k]) => CHARGE_STATUSES.includes(byKey[k]?.status)).length;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm text-mid-neutral">
          Confirm every row: set a price, or mark it <strong>Included</strong>, <strong>Not applicable</strong> or <strong>Price on assessment</strong>.
          A blank or $0 price is never treated as included.
        </p>
        <span data-testid="charges-progress" className={"shrink-0 rounded-full px-3 py-1 text-xs font-bold " + (doneCount === PRESET_CHARGES.length ? "bg-lime text-forest" : "bg-soft text-mid-neutral")}>
          {doneCount}/{PRESET_CHARGES.length} reviewed
        </span>
      </div>
      <div className="space-y-3">
        {PRESET_CHARGES.map(([key, name, category]) => (
          <ChargeRow key={key} name={name} category={category} row={byKey[key] || {}}
            error={errors?.[`charges.${key}`]}
            onChange={(patch) => upsert(key, name, category, patch)} />
        ))}
        {custom.map((r, i) => (
          <div key={i} className="relative">
            <ChargeRow name={r.name || "Custom charge"} category={r.category || "Other"} row={r} custom
              onChange={(patch) => setCustom(i, patch)} />
            <button type="button" onClick={() => removeCustom(i)} aria-label="Remove custom charge"
              className="absolute top-3 right-3 p-1.5 rounded-lg text-red-600 hover:text-red-800"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <button type="button" data-testid="charges-add-custom"
        onClick={() => onChange([...rows, { name: "", category: "Other", status: "", price: "", gst: "Inclusive of GST", unit: "each", trigger: "", approvalRequired: "", notes: "", customerWording: "" }])}
        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold">
        <Plus className="h-4 w-4" /> Add custom charge
      </button>
    </div>
  );
}

function ChargeRow({ name, category, row, onChange, error, custom }) {
  const [open, setOpen] = useState(false);
  const status = row.status || "";
  const needsPrice = status === "Fixed charge" || status === "Unit rate";
  return (
    <div className={"rounded-xl border p-4 " + (error ? "border-red-300 bg-red-50/40" : status ? "border-mint/40 bg-mint/5" : "border-border-neutral bg-white")}
      data-testid={`charge-${row.key || "custom"}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          {custom ? (
            <input value={row.name || ""} onChange={(e) => onChange({ name: e.target.value })} placeholder="Charge name"
              aria-label="Charge name" className={inputCls + " font-bold"} />
          ) : (
            <>
              <p className="text-sm font-bold text-forest">{name}</p>
              <p className="text-[11px] text-mid-neutral">{category}</p>
            </>
          )}
        </div>
        <select value={status} onChange={(e) => onChange({ status: e.target.value })} aria-label={`Status for ${name}`}
          className={"p-2 rounded-lg text-xs font-bold border focus:outline-none " + (status ? "bg-forest text-white border-forest" : "bg-soft text-forest border-border-neutral")}>
          <option value="">Set status…</option>
          {CHARGE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {needsPrice && (
          <>
            <input type="number" min="0" inputMode="decimal" value={row.price ?? ""} onChange={(e) => onChange({ price: e.target.value })}
              placeholder="Price AUD" aria-label={`Price for ${name}`} className={inputCls + " !w-28"} />
            <select value={row.gst || "Inclusive of GST"} onChange={(e) => onChange({ gst: e.target.value })} aria-label={`GST treatment for ${name}`}
              className="p-2 rounded-lg bg-soft text-xs font-semibold text-forest border border-border-neutral focus:outline-none">
              <option>Inclusive of GST</option><option>Exclusive of GST</option>
            </select>
          </>
        )}
        {status === "Unit rate" && (
          <select value={row.unit || "each"} onChange={(e) => onChange({ unit: e.target.value })} aria-label={`Unit for ${name}`}
            className="p-2 rounded-lg bg-soft text-xs font-semibold text-forest border border-border-neutral focus:outline-none">
            {CHARGE_UNITS.map((u) => <option key={u}>{u}</option>)}
          </select>
        )}
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
          className="text-xs font-bold text-mid-neutral hover:text-forest inline-flex items-center gap-1">
          Details {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
      </div>
      {error && <p role="alert" className="mt-1 text-xs font-semibold text-red-600">{error}</p>}
      {open && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-forest mb-1">Exact trigger/condition</label>
            <input value={row.trigger || ""} onChange={(e) => onChange({ trigger: e.target.value })} className={inputCls}
              placeholder="e.g. Cable run beyond 10 m" />
          </div>
          <div>
            <label className="block text-xs font-bold text-forest mb-1">Customer approval required before applying?</label>
            <select value={row.approvalRequired || ""} onChange={(e) => onChange({ approvalRequired: e.target.value })} className={inputCls}>
              <option value="">Select…</option><option>Yes</option><option>No</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-forest mb-1">Notes for the salesperson</label>
            <input value={row.notes || ""} onChange={(e) => onChange({ notes: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-bold text-forest mb-1">Customer-facing wording (optional)</label>
            <input value={row.customerWording || ""} onChange={(e) => onChange({ customerWording: e.target.value })} className={inputCls} />
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------- State-first regions picker ----
// Step 3: pick state(s) first, then tick the regions serviced in each state.
export function StateRegionsEditor({ value, onChange, errors }) {
  const states = Array.isArray(value?.states) ? value.states : [];
  const regions = Array.isArray(value?.regions) ? value.regions : [];
  const [custom, setCustom] = useState({});

  const toggleState = (st) => {
    const on = states.includes(st);
    onChange({
      states: on ? states.filter((s) => s !== st) : [...states, st],
      // dropping a state also drops its regions
      regions: on ? regions.filter((r) => r.state !== st) : regions,
    });
  };
  const toggleRegion = (st, area) => {
    const on = regions.some((r) => r.state === st && r.area === area);
    onChange({
      states,
      regions: on ? regions.filter((r) => !(r.state === st && r.area === area)) : [...regions, { state: st, area }],
    });
  };
  const addCustom = (st) => {
    const area = (custom[st] || "").trim();
    if (!area) return;
    if (!regions.some((r) => r.state === st && r.area.toLowerCase() === area.toLowerCase()))
      onChange({ states, regions: [...regions, { state: st, area }] });
    setCustom({ ...custom, [st]: "" });
  };

  return (
    <div>
      <p className="block text-sm font-bold text-forest mb-1">
        Which states/territories do you service?<span className="text-red-600 ml-0.5" aria-hidden>*</span>
      </p>
      <p className="mb-2 text-xs text-mid-neutral">Select the state first, then tick the regions you'd like to operate in.</p>
      {errors?.states && <p role="alert" className="mb-2 text-xs font-semibold text-red-600">{errors.states}</p>}
      <div className="flex flex-wrap gap-2" role="group" aria-label="States and territories serviced">
        {AU_STATES.map((st) => {
          const on = states.includes(st);
          return (
            <button key={st} type="button" aria-pressed={on} onClick={() => toggleState(st)}
              data-testid={`area-state-${st}`}
              className={"px-4 py-2 rounded-full text-sm font-bold border transition-colors " +
                (on ? "bg-forest text-white border-forest" : "bg-white text-forest border-border-neutral hover:border-mint")}>
              {st}
            </button>
          );
        })}
      </div>

      {states.map((st) => (
        <div key={st} className="mt-4 rounded-xl border border-border-neutral bg-white p-4" data-testid={`area-regions-${st}`}>
          <p className="text-sm font-extrabold text-forest mb-2 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-mint" /> Regions in {st}
          </p>
          <div className="flex flex-wrap gap-2" role="group" aria-label={`Regions in ${st}`}>
            {(REGIONS_BY_STATE[st] || []).map((area) => {
              const on = regions.some((r) => r.state === st && r.area === area);
              return (
                <button key={area} type="button" aria-pressed={on} onClick={() => toggleRegion(st, area)}
                  data-testid={`area-region-${st}-${area.replace(/[^A-Za-z0-9]+/g, "-")}`}
                  className={"px-3 py-1.5 rounded-full text-xs font-bold border transition-colors " +
                    (on ? "bg-lime text-forest border-lime" : "bg-soft text-forest border-transparent hover:border-mint")}>
                  {area}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex gap-2">
            <input value={custom[st] || ""} onChange={(e) => setCustom({ ...custom, [st]: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(st); } }}
              placeholder={`Add another region in ${st}…`} aria-label={`Add custom region in ${st}`}
              data-testid={`area-custom-${st}`}
              className={inputCls} />
            <button type="button" onClick={() => addCustom(st)} data-testid={`area-custom-add-${st}`}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-forest text-white px-3 py-2 text-xs font-bold">
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
        </div>
      ))}

      <div className="mt-3 flex items-center justify-between gap-3">
        <p data-testid="regions-count" className={"text-xs font-bold " + (regions.length > 1 ? "text-forest" : "text-mid-neutral")}>
          {regions.length === 0 ? "No regions selected yet" : `${regions.length} region${regions.length === 1 ? "" : "s"} selected`}
        </p>
        {regions.length > 1 && <span className="text-[11px] text-mint font-bold">Great coverage — more regions means better campaign results</span>}
      </div>
      {errors?.regions && <p role="alert" className="mt-1 text-xs font-semibold text-red-600">{errors.regions}</p>}
    </div>
  );
}
